import { useMemo } from 'react';
import type { ResolvedMasthead } from '@shared/types/Masthead.types';
import { resolveMasthead } from '@shared/constants/mastheads';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { decodeData } from '@shared/utils/encodeData';

// Structural subset of the generated EventDesign type — keeps this hook decoupled from codegen output.
type MastheadEventDesign = Readonly<{ mastheadId?: string | null }>;

/**
 * Single source of truth for where the mastheadId comes from.
 * Callers with event data pass their eventDesign; head/preview contexts without it may omit.
 */
export const useMasthead = (eventDesign?: Maybe<MastheadEventDesign>): ResolvedMasthead | null => {
  const [isEnabled] = useIsFeatureEnabled('websiteMastheadEnabled');
  const { masthead, templateData } = useQueryParams();
  const savedMastheadId = eventDesign?.mastheadId ?? null;

  return useMemo(() => {
    if (!isEnabled) {
      return null;
    }
    // Source priority, first non-null wins:
    // 1. mastheadId/slug query param (dev/QA driving), e.g. ?masthead=top_botanical_emerald
    // 2. templateData payload (admin template previews)
    // 3. TODO(phase 3): website designer preview-bus override
    // 4. eventDesign.mastheadId saved via BE setMasthead
    const mastheadId =
      (typeof masthead === 'string' && masthead) ||
      (typeof templateData === 'string' ? (decodeData<{ masthead?: string }>(templateData)?.masthead ?? null) : null) ||
      savedMastheadId;

    return resolveMasthead(mastheadId);
  }, [isEnabled, masthead, templateData, savedMastheadId]);
};
